import { useCallback, useEffect, useMemo, useRef, useState, type RefCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useUI } from '@/hooks/ui/useUI';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { checkInLocationService } from '@/api/domains/checkInLocation/checkInLocation.service';
import { locationEmployeesAvailableService } from '@/api/domains/checkInLocation/services/locationEmployeesAvailable.service';
import { locationEmployeesAssignedService } from '@/api/domains/checkInLocation/services/locationEmployeesAssigned.service';
import { locationEmployeesSaveService } from '@/api/domains/checkInLocation/services/locationEmployeesSave.service';
import type { LC_SaveLocationEmployeesIn, LocationEmployeeItem } from '@/api/domains/checkInLocation/checkInLocation.types';


const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const CANCEL_GUARD_DELAY_MS = SEARCH_DEBOUNCE_MS + 100;

export interface UseAssignLocationTabResult {
  locationOptions: { value: string; label: string }[];
  selectedLocationId: number | null;
  onLocationChange: (locationId: number | null) => void;

  available: LocationEmployeeItem[];
  filteredAvailable: LocationEmployeeItem[];
  isAvailableLoading: boolean;
  hasMoreAvailable: boolean;
  totalAvailable: number;
  availableSearchText: string;
  onAvailableSearch: (text: string) => void;
  availableSentinelRef: RefCallback<HTMLDivElement>;
  availableScrollContainerRef: RefCallback<HTMLDivElement>;

  assigned: LocationEmployeeItem[];
  filteredAssigned: LocationEmployeeItem[];
  isAssignedLoading: boolean;
  hasMoreAssigned: boolean;
  totalAssigned: number;
  assignedSearchText: string;
  onAssignedSearch: (text: string) => void;
  assignedSentinelRef: RefCallback<HTMLDivElement>;
  assignedScrollContainerRef: RefCallback<HTMLDivElement>;

  selectedAvailableIds: Set<number>;
  onToggleAvailable: (employeeId: number) => void;
  onSelectAllAvailable: () => void;
  isAllAvailableSelected: boolean;
  isAvailableIndeterminate: boolean;

  selectedAssignedIds: Set<number>;
  onToggleAssigned: (employeeId: number) => void;
  onSelectAllAssigned: () => void;
  isAllAssignedSelected: boolean;
  isAssignedIndeterminate: boolean;

  onAdd: () => void;
  onRemove: () => void;
  onDoubleClickAvailable: (employee: LocationEmployeeItem) => void;
  onDoubleClickAssigned: (employee: LocationEmployeeItem) => void;
  onAddAllFromServer: () => void;
  onRemoveAllFromServer: () => void;
  isAddAllPending: boolean;
  isRemoveAllPending: boolean;

  isDirty: boolean;
  isSaving: boolean;
  canEdit: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
}

export function useAssignLocationTab(): UseAssignLocationTabResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const { canEdit } = usePagePermissions();
  const { createSignal: createAvailableSignal, abort: abortAvailable } = useAbortController();
  const { createSignal: createAssignedSignal, abort: abortAssigned } = useAbortController();

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);

  const loadLocationOptions = useCallback(async () => {
    const response = await checkInLocationService.list({ isActive: true, pageSize: 1000 });
    if (response.success && response.data) {
      setLocationOptions(
        response.data.items.map((location) => ({
          value: String(location.id),
          label: location.name,
        })),
      );
    } else {
      showApiError(response, translate('checkInLocation:assignLocation.messages.loadError'));
    }
  }, [showApiError, translate]);

  useEffect(() => { void loadLocationOptions(); }, [loadLocationOptions]);

  const [available, setAvailable] = useState<LocationEmployeeItem[]>([]);
  const [isAvailableLoading, setIsAvailableLoading] = useState(false);
  const [hasMoreAvailable, setHasMoreAvailable] = useState(true);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const availablePageRef = useRef(1);
  const availableAccumulatedRef = useRef(0);
  const addedAvailableFilteredRef = useRef(0);
  const fetchAvailableRef = useRef<() => Promise<void>>(async () => {});
  const isFetchingAvailableRef = useRef(false);
  const [availableSentinelEl, setAvailableSentinelEl] = useState<HTMLDivElement | null>(null);
  const [availableContainerEl, setAvailableContainerEl] = useState<HTMLDivElement | null>(null);

  const availableSentinelRef = useCallback(
    (element: HTMLDivElement | null) => { setAvailableSentinelEl(element); },
    [],
  );
  const availableScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => { setAvailableContainerEl(element); },
    [],
  );

  const [assigned, setAssigned] = useState<LocationEmployeeItem[]>([]);
  const [isAssignedLoading, setIsAssignedLoading] = useState(false);
  const [hasMoreAssigned, setHasMoreAssigned] = useState(true);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const assignedPageRef = useRef(1);
  const assignedAccumulatedRef = useRef(0);
  const removedAssignedFilteredRef = useRef(0);
  const fetchAssignedRef = useRef<() => Promise<void>>(async () => {});
  const isFetchingAssignedRef = useRef(false);
  const [assignedSentinelEl, setAssignedSentinelEl] = useState<HTMLDivElement | null>(null);
  const [assignedContainerEl, setAssignedContainerEl] = useState<HTMLDivElement | null>(null);

  const assignedSentinelRef = useCallback(
    (element: HTMLDivElement | null) => { setAssignedSentinelEl(element); },
    [],
  );
  const assignedScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => { setAssignedContainerEl(element); },
    [],
  );

  const [availableSearchText, setAvailableSearchText] = useState('');
  const [assignedSearchText, setAssignedSearchText] = useState('');
  const debouncedAvailableSearchText = useDebounce(availableSearchText, SEARCH_DEBOUNCE_MS);
  const debouncedAssignedSearchText = useDebounce(assignedSearchText, SEARCH_DEBOUNCE_MS);

  const originalAssignedRef = useRef<LocationEmployeeItem[]>([]);
  const originalAvailableRef = useRef<LocationEmployeeItem[]>([]);
  const originalTotalAssignedRef = useRef(0);
  const originalTotalAvailableRef = useRef(0);
  const originalHasMoreAssignedRef = useRef(false);
  const originalHasMoreAvailableRef = useRef(false);
  const originalsStoredRef = useRef(false);
  const cancelAvailableResetRef = useRef(false);
  const cancelAssignedResetRef = useRef(false);
  const cancelGuardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(new Set());
  const [selectedAssignedIds, setSelectedAssignedIds] = useState<Set<number>>(new Set());

  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [isAddAllPending, setIsAddAllPending] = useState(false);
  const [isRemoveAllPending, setIsRemoveAllPending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const addedIdsRef = useRef<Set<number>>(new Set());
  const removedIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => { addedIdsRef.current = addedIds; }, [addedIds]);
  useEffect(() => { removedIdsRef.current = removedIds; }, [removedIds]);

  const isAddAllPendingRef = useRef(false);
  const isRemoveAllPendingRef = useRef(false);
  useEffect(() => { isAddAllPendingRef.current = isAddAllPending; }, [isAddAllPending]);
  useEffect(() => { isRemoveAllPendingRef.current = isRemoveAllPending; }, [isRemoveAllPending]);

  const fetchAvailable = useCallback(async () => {
    if (!selectedLocationId) return;
    if (isFetchingAvailableRef.current) return;
    if (isRemoveAllPendingRef.current) return;

    isFetchingAvailableRef.current = true;
    setIsAvailableLoading(true);
    const signal = createAvailableSignal();
    const page = availablePageRef.current;

    const response = await locationEmployeesAvailableService.getAvailable(
      selectedLocationId,
      {
        page,
        pageSize: PAGE_SIZE,
        ...(debouncedAvailableSearchText ? { searchText: debouncedAvailableSearchText } : {}),
      },
      signal,
    );

    if (response.error?.code === 'ABORTED') {
      isFetchingAvailableRef.current = false;
      setIsAvailableLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.totalRecords;
      const mappedData = response.data.items.filter(
        (employee) => !addedIdsRef.current.has(employee.employeeId),
      );
      const addedFilteredInPage = response.data.items.length - mappedData.length;

      if (page === 1) {
        addedAvailableFilteredRef.current = addedFilteredInPage;
        const adjustedTotal = totalRecords
          - addedAvailableFilteredRef.current
          + removedIdsRef.current.size;
        setTotalAvailable(adjustedTotal);
        availableAccumulatedRef.current = mappedData.length;
        setAvailable(mappedData);
      } else {
        addedAvailableFilteredRef.current += addedFilteredInPage;
        availableAccumulatedRef.current += mappedData.length;
        setAvailable((previous) => [...previous, ...mappedData]);
      }

      setHasMoreAvailable(
        mappedData.length > 0 &&
        availableAccumulatedRef.current < (totalRecords - addedAvailableFilteredRef.current),
      );
    } else {
      showApiError(response, translate('checkInLocation:assignLocation.messages.loadError'));
    }

    isFetchingAvailableRef.current = false;
    setIsAvailableLoading(false);
  }, [
    selectedLocationId, debouncedAvailableSearchText,
    createAvailableSignal, showApiError, translate,
  ]);

  useEffect(() => { fetchAvailableRef.current = fetchAvailable; }, [fetchAvailable]);

  useEffect(() => {
    if (cancelAvailableResetRef.current) return;
    if (isRemoveAllPending) return;
    availablePageRef.current = 1;
    availableAccumulatedRef.current = 0;
    addedAvailableFilteredRef.current = 0;
    isFetchingAvailableRef.current = false;
    setAvailable([]);
    setHasMoreAvailable(true);
  }, [debouncedAvailableSearchText, isRemoveAllPending]);

  useEffect(() => { void fetchAvailable(); }, [fetchAvailable]);

  useEffect(() => {
    if (!availableSentinelEl || !availableContainerEl ||
      !hasMoreAvailable || isRemoveAllPending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingAvailableRef.current) {
          availablePageRef.current += 1;
          void fetchAvailableRef.current();
        }
      },
      { root: availableContainerEl, threshold: 0 },
    );

    observer.observe(availableSentinelEl);
    return () => observer.disconnect();
  }, [hasMoreAvailable, availableSentinelEl, availableContainerEl, isRemoveAllPending]);

  const fetchAssigned = useCallback(async () => {
    if (!selectedLocationId) return;
    if (isFetchingAssignedRef.current) return;
    if (isAddAllPendingRef.current) return;

    isFetchingAssignedRef.current = true;
    setIsAssignedLoading(true);
    const signal = createAssignedSignal();
    const page = assignedPageRef.current;

    const response = await locationEmployeesAssignedService.getAssigned(
      selectedLocationId,
      {
        page,
        pageSize: PAGE_SIZE,
        ...(debouncedAssignedSearchText ? { searchText: debouncedAssignedSearchText } : {}),
      },
      signal,
    );

    if (response.error?.code === 'ABORTED') {
      isFetchingAssignedRef.current = false;
      setIsAssignedLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.totalRecords;
      const mappedData = response.data.items.filter(
        (employee) => !removedIdsRef.current.has(employee.employeeId),
      );
      const removedFilteredInPage = response.data.items.length - mappedData.length;

      if (page === 1) {
        removedAssignedFilteredRef.current = removedFilteredInPage;
        const adjustedTotal = totalRecords
          - removedAssignedFilteredRef.current
          + addedIdsRef.current.size;
        setTotalAssigned(adjustedTotal);
        assignedAccumulatedRef.current = mappedData.length;
        setAssigned(mappedData);
      } else {
        removedAssignedFilteredRef.current += removedFilteredInPage;
        assignedAccumulatedRef.current += mappedData.length;
        setAssigned((previous) => [...previous, ...mappedData]);
      }

      setHasMoreAssigned(
        mappedData.length > 0 &&
        assignedAccumulatedRef.current < (totalRecords - removedAssignedFilteredRef.current),
      );
    } else {
      showApiError(response, translate('checkInLocation:assignLocation.messages.loadError'));
    }

    isFetchingAssignedRef.current = false;
    setIsAssignedLoading(false);
  }, [
    selectedLocationId, debouncedAssignedSearchText,
    createAssignedSignal, showApiError, translate,
  ]);

  useEffect(() => { fetchAssignedRef.current = fetchAssigned; }, [fetchAssigned]);

  useEffect(() => {
    if (cancelAssignedResetRef.current) return;
    if (isAddAllPending) return;
    assignedPageRef.current = 1;
    assignedAccumulatedRef.current = 0;
    removedAssignedFilteredRef.current = 0;
    isFetchingAssignedRef.current = false;
    setAssigned([]);
    setHasMoreAssigned(true);
  }, [debouncedAssignedSearchText, isAddAllPending]);

  useEffect(() => { void fetchAssigned(); }, [fetchAssigned]);

  useEffect(() => {
    if (!assignedSentinelEl || !assignedContainerEl || !hasMoreAssigned || isAddAllPending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingAssignedRef.current) {
          assignedPageRef.current += 1;
          void fetchAssignedRef.current();
        }
      },
      { root: assignedContainerEl, threshold: 0 },
    );

    observer.observe(assignedSentinelEl);
    return () => observer.disconnect();
  }, [hasMoreAssigned, assignedSentinelEl, assignedContainerEl, isAddAllPending]);

  const onLocationChange = useCallback((locationId: number | null) => {
    setSelectedLocationId(locationId);
    setAvailable([]);
    setAssigned([]);
    setSelectedAvailableIds(new Set());
    setSelectedAssignedIds(new Set());
    availablePageRef.current = 1;
    availableAccumulatedRef.current = 0;
    addedAvailableFilteredRef.current = 0;
    assignedPageRef.current = 1;
    assignedAccumulatedRef.current = 0;
    removedAssignedFilteredRef.current = 0;
    setHasMoreAvailable(true);
    setHasMoreAssigned(true);
    setTotalAvailable(0);
    setTotalAssigned(0);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setIsAddAllPending(false);
    setIsRemoveAllPending(false);
    originalAssignedRef.current = [];
    originalAvailableRef.current = [];
    originalsStoredRef.current = false;
    isFetchingAvailableRef.current = false;
    isFetchingAssignedRef.current = false;
    setAvailableSearchText('');
    setAssignedSearchText('');
    setIsDirty(false);
    if (locationId !== null) {
      setIsAvailableLoading(true);
      setIsAssignedLoading(true);
    }
  }, []);

  useEffect(() => {
    if (!selectedLocationId) return;
    if (isAvailableLoading || isAssignedLoading) return;
    if (originalsStoredRef.current) return;
    originalsStoredRef.current = true;
    originalAssignedRef.current = assigned;
    originalAvailableRef.current = available;
    originalTotalAssignedRef.current = totalAssigned;
    originalTotalAvailableRef.current = totalAvailable;
    originalHasMoreAssignedRef.current = hasMoreAssigned;
    originalHasMoreAvailableRef.current = hasMoreAvailable;
  }, [
    isAvailableLoading, isAssignedLoading, assigned, available,
    selectedLocationId, totalAssigned, totalAvailable, hasMoreAssigned, hasMoreAvailable,
  ]);

  const filteredAvailable = useMemo(() => {
    const query = availableSearchText.toLowerCase().trim();
    const withoutMoved = available.filter((e) => !addedIds.has(e.employeeId));
    if (!query) return withoutMoved;
    return withoutMoved.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(query) ||
        employee.employeeTaxIdentificationNumber.toLowerCase().includes(query),
    );
  }, [available, availableSearchText, addedIds]);

  const filteredAssigned = useMemo(() => {
    const query = assignedSearchText.toLowerCase().trim();
    const withoutMoved = assigned.filter((e) => !removedIds.has(e.employeeId));
    if (!query) return withoutMoved;
    return withoutMoved.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(query) ||
        employee.employeeTaxIdentificationNumber.toLowerCase().includes(query),
    );
  }, [assigned, assignedSearchText, removedIds]);

  const onToggleAvailable = useCallback((employeeId: number) => {
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) { next.delete(employeeId); } else { next.add(employeeId); }
      return next;
    });
  }, []);

  const isAllAvailableSelected = useMemo(
    () =>
      filteredAvailable.length > 0 &&
      filteredAvailable.every((e) => selectedAvailableIds.has(e.employeeId)),
    [filteredAvailable, selectedAvailableIds],
  );

  const isAvailableIndeterminate = useMemo(
    () =>
      !isAllAvailableSelected &&
      filteredAvailable.some((e) => selectedAvailableIds.has(e.employeeId)),
    [filteredAvailable, selectedAvailableIds, isAllAvailableSelected],
  );

  const onSelectAllAvailable = useCallback(() => {
    if (isAllAvailableSelected) {
      setSelectedAvailableIds((previous) => {
        const next = new Set(previous);
        filteredAvailable.forEach((e) => next.delete(e.employeeId));
        return next;
      });
    } else {
      setSelectedAvailableIds((previous) => {
        const next = new Set(previous);
        filteredAvailable.forEach((e) => next.add(e.employeeId));
        return next;
      });
    }
  }, [filteredAvailable, isAllAvailableSelected]);

  const onToggleAssigned = useCallback((employeeId: number) => {
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) { next.delete(employeeId); } else { next.add(employeeId); }
      return next;
    });
  }, []);

  const isAllAssignedSelected = useMemo(
    () =>
      filteredAssigned.length > 0 &&
      filteredAssigned.every((e) => selectedAssignedIds.has(e.employeeId)),
    [filteredAssigned, selectedAssignedIds],
  );

  const isAssignedIndeterminate = useMemo(
    () =>
      !isAllAssignedSelected &&
      filteredAssigned.some((e) => selectedAssignedIds.has(e.employeeId)),
    [filteredAssigned, selectedAssignedIds, isAllAssignedSelected],
  );

  const onSelectAllAssigned = useCallback(() => {
    if (isAllAssignedSelected) {
      setSelectedAssignedIds((previous) => {
        const next = new Set(previous);
        filteredAssigned.forEach((e) => next.delete(e.employeeId));
        return next;
      });
    } else {
      setSelectedAssignedIds((previous) => {
        const next = new Set(previous);
        filteredAssigned.forEach((e) => next.add(e.employeeId));
        return next;
      });
    }
  }, [filteredAssigned, isAllAssignedSelected]);

  const onAdd = useCallback(() => {
    const movedCount = selectedAvailableIds.size;
    setAddedIds((previous) => {
      const next = new Set(previous);
      selectedAvailableIds.forEach((id) => next.add(id));
      return next;
    });
    setRemovedIds((previous) => {
      const next = new Set(previous);
      selectedAvailableIds.forEach((id) => next.delete(id));
      return next;
    });
    setAvailable((previous) => previous.filter((e) => !selectedAvailableIds.has(e.employeeId)));
    setAssigned((previous) => [
      ...previous,
      ...available.filter((e) => selectedAvailableIds.has(e.employeeId)),
    ]);
    setTotalAvailable((previous) => previous - movedCount);
    setTotalAssigned((previous) => previous + movedCount);
    setSelectedAvailableIds(new Set());
    setIsDirty(true);
  }, [available, selectedAvailableIds]);

  const onRemove = useCallback(() => {
    const movedCount = selectedAssignedIds.size;
    setRemovedIds((previous) => {
      const next = new Set(previous);
      selectedAssignedIds.forEach((id) => next.add(id));
      return next;
    });
    setAddedIds((previous) => {
      const next = new Set(previous);
      selectedAssignedIds.forEach((id) => next.delete(id));
      return next;
    });
    setAssigned((previous) => previous.filter((e) => !selectedAssignedIds.has(e.employeeId)));
    setAvailable((previous) => [
      ...previous,
      ...assigned.filter((e) => selectedAssignedIds.has(e.employeeId)),
    ]);
    setTotalAssigned((previous) => previous - movedCount);
    setTotalAvailable((previous) => previous + movedCount);
    setSelectedAssignedIds(new Set());
    setIsDirty(true);
  }, [assigned, selectedAssignedIds]);

  const onDoubleClickAvailable = useCallback((employee: LocationEmployeeItem) => {
    setAddedIds((previous) => {
      const next = new Set(previous); next.add(employee.employeeId); return next;
    });
    setRemovedIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setAvailable((previous) => previous.filter((e) => e.employeeId !== employee.employeeId));
    setAssigned((previous) => [...previous, employee]);
    setTotalAvailable((previous) => previous - 1);
    setTotalAssigned((previous) => previous + 1);
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setIsDirty(true);
  }, []);

  const onDoubleClickAssigned = useCallback((employee: LocationEmployeeItem) => {
    setRemovedIds((previous) => {
      const next = new Set(previous); next.add(employee.employeeId); return next;
    });
    setAddedIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setAssigned((previous) => previous.filter((e) => e.employeeId !== employee.employeeId));
    setAvailable((previous) => [...previous, employee]);
    setTotalAssigned((previous) => previous - 1);
    setTotalAvailable((previous) => previous + 1);
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setIsDirty(true);
  }, []);

  const onAddAllFromServer = useCallback(() => {
    setIsAddAllPending(true);
    setIsRemoveAllPending(false);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setAssigned((previous) => [...previous, ...available]);
    setAvailable([]);
    setTotalAssigned((previous) => previous + totalAvailable);
    setTotalAvailable(0);
    setHasMoreAvailable(false);
    setHasMoreAssigned(hasMoreAssigned || available.length < totalAvailable);
    availablePageRef.current = 1;
    availableAccumulatedRef.current = 0;
    setSelectedAvailableIds(new Set());
    setIsDirty(true);
  }, [available, totalAvailable, hasMoreAssigned]);

  const onRemoveAllFromServer = useCallback(() => {
    setIsRemoveAllPending(true);
    setIsAddAllPending(false);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setAvailable((previous) => [...previous, ...assigned]);
    setAssigned([]);
    setTotalAvailable((previous) => previous + totalAssigned);
    setTotalAssigned(0);
    setHasMoreAssigned(false);
    setHasMoreAvailable(hasMoreAvailable || assigned.length < totalAssigned);
    assignedPageRef.current = 1;
    assignedAccumulatedRef.current = 0;
    setSelectedAssignedIds(new Set());
    setIsDirty(true);
  }, [assigned, totalAssigned, hasMoreAvailable]);

  const onAvailableSearch = useCallback((text: string) => { setAvailableSearchText(text); }, []);
  const onAssignedSearch = useCallback((text: string) => { setAssignedSearchText(text); }, []);

  const [isSaving, setIsSaving] = useState(false);

  const onSave = useCallback(async () => {
    if (!selectedLocationId) return;
    setIsSaving(true);

    let body: LC_SaveLocationEmployeesIn;
    if (isAddAllPending) {
      body = {
        addAll: true,
        removeAll: false,
        employeeIdsToAdd: null,
        employeeIdsToRemove: removedIds.size > 0 ? [...removedIds] : null,
      };
    } else if (isRemoveAllPending) {
      body = {
        addAll: false,
        removeAll: true,
        employeeIdsToAdd: addedIds.size > 0 ? [...addedIds] : null,
        employeeIdsToRemove: null,
      };
    } else {
      body = {
        addAll: false,
        removeAll: false,
        employeeIdsToAdd: addedIds.size > 0 ? [...addedIds] : null,
        employeeIdsToRemove: removedIds.size > 0 ? [...removedIds] : null,
      };
    }

    const response = await locationEmployeesSaveService.save(selectedLocationId, body);
    if (response.success) {
      showNotification('success', translate('checkInLocation:assignLocation.messages.saveSuccess'));

      setAvailable([]);
      setAssigned([]);
      setSelectedAvailableIds(new Set());
      setSelectedAssignedIds(new Set());
      setAddedIds(new Set());
      setRemovedIds(new Set());
      setIsAddAllPending(false);
      setIsRemoveAllPending(false);
      availablePageRef.current = 1;
      availableAccumulatedRef.current = 0;
      addedAvailableFilteredRef.current = 0;
      assignedPageRef.current = 1;
      assignedAccumulatedRef.current = 0;
      removedAssignedFilteredRef.current = 0;
      setHasMoreAvailable(true);
      setHasMoreAssigned(true);
      isFetchingAvailableRef.current = false;
      isFetchingAssignedRef.current = false;
      originalsStoredRef.current = false;
      isAddAllPendingRef.current = false;
      isRemoveAllPendingRef.current = false;
      setAvailableSearchText('');
      setAssignedSearchText('');
      setIsDirty(false);

      await Promise.all([fetchAvailableRef.current(), fetchAssignedRef.current()]);
    } else {
      showApiError(response, translate('checkInLocation:assignLocation.messages.saveError'));
    }

    setIsSaving(false);
  }, [
    selectedLocationId, isAddAllPending, isRemoveAllPending,
    addedIds, removedIds, showApiError, showNotification, translate,
  ]);

  useEffect(() => () => {
    if (cancelGuardTimerRef.current !== null) clearTimeout(cancelGuardTimerRef.current);
  }, []);

  const onCancel = useCallback(() => {
    abortAvailable();
    abortAssigned();
    if (cancelGuardTimerRef.current !== null) clearTimeout(cancelGuardTimerRef.current);
    cancelAvailableResetRef.current = true;
    cancelAssignedResetRef.current = true;
    cancelGuardTimerRef.current = setTimeout(() => {
      cancelAvailableResetRef.current = false;
      cancelAssignedResetRef.current = false;
      cancelGuardTimerRef.current = null;
    }, CANCEL_GUARD_DELAY_MS);
    setAssigned(originalAssignedRef.current);
    setAvailable(originalAvailableRef.current);
    setTotalAssigned(originalTotalAssignedRef.current);
    setTotalAvailable(originalTotalAvailableRef.current);
    setHasMoreAssigned(originalHasMoreAssignedRef.current);
    setHasMoreAvailable(originalHasMoreAvailableRef.current);
    availablePageRef.current = Math.ceil(originalAvailableRef.current.length / PAGE_SIZE) || 1;
    availableAccumulatedRef.current = originalAvailableRef.current.length;
    assignedPageRef.current = Math.ceil(originalAssignedRef.current.length / PAGE_SIZE) || 1;
    assignedAccumulatedRef.current = originalAssignedRef.current.length;
    setSelectedAvailableIds(new Set());
    setSelectedAssignedIds(new Set());
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setIsAddAllPending(false);
    setIsRemoveAllPending(false);
    setAvailableSearchText('');
    setAssignedSearchText('');
    setIsDirty(false);
  }, [abortAvailable, abortAssigned]);

  return {
    locationOptions,
    selectedLocationId,
    onLocationChange,
    available,
    filteredAvailable,
    isAvailableLoading,
    hasMoreAvailable,
    totalAvailable,
    availableSearchText,
    onAvailableSearch,
    availableSentinelRef,
    availableScrollContainerRef,
    assigned,
    filteredAssigned,
    isAssignedLoading,
    hasMoreAssigned,
    totalAssigned,
    assignedSearchText,
    onAssignedSearch,
    assignedSentinelRef,
    assignedScrollContainerRef,
    selectedAvailableIds,
    onToggleAvailable,
    onSelectAllAvailable,
    isAllAvailableSelected,
    isAvailableIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickAvailable,
    onDoubleClickAssigned,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    canEdit,
    onSave,
    onCancel,
  };
}
